import { useState, useEffect } from "react";
import { X, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface TagSelectorProps {
  value: string[];
  onChange: (tags: string[]) => void;
  availableTags: string[];
  placeholder?: string;
  allowCustom?: boolean;
  maxTags?: number;
}

export const TagSelector = ({
  value,
  onChange,
  availableTags,
  placeholder = "Select or add tags...",
  allowCustom = true,
  maxTags,
}: TagSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Filter available tags that aren't already selected
  const filteredTags = availableTags.filter(
    (tag) =>
      !value.includes(tag) &&
      tag.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleAddTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (!trimmedTag) return;
    if (value.includes(trimmedTag)) return;
    if (maxTags && value.length >= maxTags) return;
    
    onChange([...value, trimmedTag]);
    setInputValue("");
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim() && allowCustom) {
        handleAddTag(inputValue);
      }
    }
  };

  return (
    <div className="space-y-2">
      {/* Selected Tags */}
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {value.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md"
          >
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
            >
              <X size={10} />
            </button>
          </Badge>
        ))}
      </div>

      {/* Tag Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start text-muted-foreground font-normal"
          >
            <Plus size={16} className="mr-2" />
            {placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-2" align="start">
          <div className="space-y-2">
            {allowCustom && (
              <div className="flex gap-2">
                <Input
                  placeholder="Type to search or add..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="h-8"
                />
                {inputValue.trim() && !availableTags.includes(inputValue.trim()) && (
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 shrink-0"
                    onClick={() => handleAddTag(inputValue)}
                  >
                    Add
                  </Button>
                )}
              </div>
            )}
            
            <div className="max-h-[200px] overflow-y-auto">
              {filteredTags.length === 0 && !inputValue ? (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No more tags available
                </p>
              ) : filteredTags.length === 0 && inputValue ? (
                <p className="text-sm text-muted-foreground text-center py-2">
                  {allowCustom ? 'Press Enter or click Add to create' : 'No matching tags'}
                </p>
              ) : (
                <div className="space-y-1">
                  {filteredTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        handleAddTag(tag);
                        setOpen(false);
                      }}
                      className={cn(
                        "w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted flex items-center justify-between",
                        value.includes(tag) && "bg-muted"
                      )}
                    >
                      {tag}
                      {value.includes(tag) && <Check size={14} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
